<script setup lang="ts">
import { isEqual } from "lodash-es";
import { computed, ref, watch } from "vue";

import { getBoardDiff } from "@/backend/read/BackendReader";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import LoadingIndicator from "@/components/ui/LoadingIndicator.vue";
import type { ChangeTypeItem, StickyChange } from "@/model/change";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";
import { fadeOutAllCardMarks, resetAllCardMarks, resetCardMark } from "@/store/helpers/mark";

import ChangeTypesFilter from "./internal/ChangeTypesFilter.vue";
import DiffsBySticky from "./internal/DiffsBySticky.vue";

const changeTypes = computed({
  get: () => store?.selectedChangeTypes,
  set: (values: ChangeTypeItem[]) => store.selectChangeTypes(values),
});

const store = useActivityStore();
const changes = ref<StickyChange[]>([]);
const loading = ref(false);

watch(
  () => ({ ...store.allFilters }),
  async (newState, prevState) => {
    const board = useBoardStore().board;
    // somehow the watch triggers sometimes even if neither board nor date change
    // check it to avoid multiple calls to the backend
    const somethingChanged = !isEqual(newState, prevState);

    if (board && somethingChanged) {
      if (store.board && newState.date) {
        const filters = useActivityStore().serverFilters;

        loading.value = true;
        const loadEvents = getBoardDiff(
          board.id,
          newState.date.fromDate,
          newState.date.toDate,
          filters,
          [...newState.changeTypes, "!a_center", "!stack_pos"],
        );
        // wait until ToggleButton finishes transition; otherwise, animation
        // will freeze for a moment due to resources allocation for drawing
        // a substantial amount of list items
        const [ret] = await Promise.all([
          loadEvents,
          new Promise<void>((resolve) => setTimeout(() => resolve(), 300)),
        ]);
        changes.value = ret;
        loading.value = false;
      } else {
        changes.value = [];
        store.resetFilters();
      }

      updateHighlightedCards();
    }
  },
  { immediate: true },
);

/**
 * Highlight the cards that match the current filters
 */
const updateHighlightedCards = () => {
  const board = useBoardStore().board;

  if (board && store.board && store.selectedDate && !loading.value) {
    fadeOutAllCardMarks(board);
    for (const change of changes.value) {
      resetCardMark(board.cards[change.stickyId]);
    }
  } else if (board) {
    resetAllCardMarks(board);
  }
};
</script>

<template>
  <SidePanelContent v-focus-trap v-autofocus>
    <template #sub-title>
      <div class="panel-header">
        <h2 class="subtitle">{{ $t("boardDiff.compare.title") }}</h2>
        <p class="description">{{ $t("boardDiff.compare.description") }}</p>
        <ChangeTypesFilter v-model="changeTypes" />
      </div>
    </template>

    <ComplexList>
      <DiffsBySticky :changes />

      <ListEmpty v-if="loading">
        <LoadingIndicator />
      </ListEmpty>

      <ListEmpty v-if="changes.length === 0 && !loading">
        {{ $t("searchResults.noResults") }}
      </ListEmpty>
    </ComplexList>
  </SidePanelContent>
</template>
